import type { UserContextResponse } from "@lib/ShiOneClient";

export interface SwitcherStorefront {
  optionId: string;
  orgUnitId: number;
  name: string;
  logoUrl?: string;
}

export interface SwitcherTenantGroup {
  optionId: string;
  tenantId: string;
  tenantName: string;
  isCurrent: boolean;
  storefronts: SwitcherStorefront[];
}

export interface SwitcherData {
  tenantGroups: SwitcherTenantGroup[];
  /** Option id of the active selection: the current storefront, or the current tenant when none. */
  currentOptionId: string | null;
  /** Option id of a storefront switch whose cart check did not finish; re-selecting it retries the switch. */
  pendingOptionId: string | null;
  canSwitchTenant: boolean;
  canSwitchStorefront: boolean;
}

export type SwitcherSelection =
  | { kind: "tenant"; tenantId: string }
  | { kind: "storefront"; tenantId: string; orgUnitId: number };

export const tenantOptionId = (tenantId: string) => `tenant:${tenantId}`;
export const storefrontOptionId = (tenantId: string, orgUnitId: number) =>
  `storefront:${tenantId}:${orgUnitId}`;

export function parseOptionId(optionId: string): SwitcherSelection | null {
  const [kind, tenantId, orgUnitId] = optionId.split(":");
  if (kind === "tenant" && tenantId) {
    return { kind, tenantId };
  }
  if (kind === "storefront" && tenantId && Number(orgUnitId) > 0) {
    return { kind, tenantId, orgUnitId: Number(orgUnitId) };
  }
  return null;
}

/** SHI.com stores storefront logos as site-relative paths. */
export function resolveLogoUrl(
  logoPath: string | undefined,
  shiComBaseUrl: string,
): string | undefined {
  if (!logoPath) {
    return undefined;
  }
  if (/^https?:\/\//i.test(logoPath)) {
    return logoPath;
  }
  const base = shiComBaseUrl.replace(/\/+$/, "");
  return `${base}/${logoPath.replace(/^\/+/, "")}`;
}

export function toSwitcherData(
  response: UserContextResponse | undefined,
  shiComBaseUrl: string,
): SwitcherData {
  const tenants = response?.tenants ?? [];
  let currentOptionId: string | null = null;

  const tenantGroups = tenants.map((tenant): SwitcherTenantGroup => {
    const storefronts = (tenant.storefronts ?? []).map((storefront) => {
      const optionId = storefrontOptionId(
        tenant.tenantId,
        storefront.orgUnitId,
      );
      if (tenant.isCurrent && storefront.isCurrent) {
        currentOptionId = optionId;
      }
      return {
        optionId,
        orgUnitId: storefront.orgUnitId,
        name: storefront.name ?? "",
        logoUrl: resolveLogoUrl(storefront.logoPath, shiComBaseUrl),
      };
    });

    return {
      optionId: tenantOptionId(tenant.tenantId),
      tenantId: tenant.tenantId,
      tenantName: tenant.name ?? "",
      isCurrent: tenant.isCurrent,
      storefronts,
    };
  });

  const currentTenant = tenantGroups.find((group) => group.isCurrent);
  const pendingSwitch = response?.pendingSwitch;
  return {
    tenantGroups,
    currentOptionId: currentOptionId ?? currentTenant?.optionId ?? null,
    pendingOptionId: pendingSwitch
      ? storefrontOptionId(pendingSwitch.tenantId, pendingSwitch.orgUnitId)
      : null,
    canSwitchTenant: response?.canSwitchTenant ?? false,
    canSwitchStorefront: response?.canSwitchStorefront ?? false,
  };
}
